import React from 'react';
import { UserOutlined } from '@ant-design/icons';
import { Tooltip } from '@clockout-dev/design-elements';
import SectionCard from '../../../common/sectionCard/SectionCard';
import UserCardRow from './UserCardRow';
import UserStatePill from '../../../common/userStatePill/UserStatePill';
import { getJourneyStage } from '../../../reports/elements/journeyStageTag.constants';
import PayrollStatusPill from '../../../common/payrollStatusPill/PayrollStatusPill';
import { formatNumberWithCommas } from '../../../../utils/numbers';
import styles from './styles/user-details-card.module.scss';

/**
 * "User Details" card (Figma 5747:1750). Wraps the shared
 * `SectionCard` shell (same shell used by Team / Treasury / Billing
 * cards) so the user-journey page renders with the standard admin
 * card chrome.
 *
 * Rows:
 *  - Journey Stage    — pill driven by `consumer.journeyStage`, the SAME
 *    token and vocabulary the Reports Users table shows for this person
 *    (`journeyStageTag.constants`), with the stage definition on hover.
 *  - Payroll          — `PayrollStatusPill` from `consumer.payrollStatus` /
 *    `consumer.connectionsCount`: the SAME field and component the Reports
 *    Users table's Payroll column renders for this person (KAN-617/618), so
 *    the two surfaces cannot disagree.
 *  - Available Balance — currency. TODO(backend): not yet exposed
 *    by `fetchConsumerUser`, so the row falls back to "—".
 *  - Pay Cycle        — start/end labels from the pre-parsed
 *    `payPeriod` prop (parsed in the controller).
 *  - Next Payday      — pay-period end + 1 day (also pre-parsed).
 *
 * @param {Object} props
 * @param {Object} props.consumer Consumer payload from `fetchConsumerUser`.
 * @param {{ payCycleLabel: string, nextPaydayLabel: string } | null} [props.payPeriod]
 */
function UserDetailsCard({ consumer, payPeriod }) {
    // `consumer.journeyStage` comes from the same server resolver as the
    // Users table; anything missing or unrecognised reads as Unknown — never
    // inferred client-side. The stage tones are the pill tones.
    const stage = getJourneyStage(consumer?.journeyStage);
    const statusTone = stage.tone;

    const availableBalance = consumer?.availableBalance;
    const availableBalanceLabel = typeof availableBalance === 'number'
        ? `$${formatNumberWithCommas(availableBalance, true)}`
        : '—';

    return (
        <SectionCard icon={<UserOutlined />} title="User Details">
            <div className={styles.rows}>
                <UserCardRow
                    label="Journey Stage"
                    value={(
                        <Tooltip title={stage.description} placement="left">
                            <span aria-label={`Journey stage: ${stage.label}`}>
                                <UserStatePill tone={statusTone} label={stage.label} />
                            </span>
                        </Tooltip>
                    )}
                />
                <UserCardRow
                    label="Payroll"
                    value={
                        <PayrollStatusPill
                            state={consumer?.payrollStatus}
                            connectionsCount={consumer?.connectionsCount}
                            warning={consumer?.payrollStatusWarning}
                        />
                    }
                />
                <UserCardRow label="Available Balance" value={availableBalanceLabel} />
                <UserCardRow label="Pay Cycle" value={payPeriod?.payCycleLabel || '—'} />
                <UserCardRow label="Next Payday" value={payPeriod?.nextPaydayLabel || '—'} />
            </div>
        </SectionCard>
    );
}

export default UserDetailsCard;
